import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext'
import { useChat } from '@/contexts/ChatContext'
import { messagesApi } from '@/lib/api/services/messages'
import { conversationsApi } from '@/lib/api/services/conversations'
import { dismissConversationNotifications } from '@/lib/notifications/dismissNotifications'

interface UnreadCounts {
    total: number
    byConversation: Record<string, number>
    conversationsCount: number
}

interface UnreadMessagesContextType {
    totalUnreadCount: number
    unreadCounts: Record<string, number>
    totalUnreadConversations: number
    unreadByPlatform: Record<string, number>
    isLoading: boolean
    error: Error | null
    markConversationAsRead: (conversationId: string) => Promise<void>
    incrementUnreadCount: (conversationId: string, platform?: string) => void
    getUnreadCount: (conversationId: string) => number
    hasUnreadMessages: (conversationId: string) => boolean
    refreshUnreadCounts: () => Promise<void>
}

const UnreadMessagesContext = createContext<UnreadMessagesContextType | undefined>(undefined)

export function UnreadMessagesProvider({ children }: { children: React.ReactNode }) {
    const businessAccountContext = useBusinessAccountContext()
    const chatContext = useChat()
    const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({
        total: 0,
        byConversation: {},
        conversationsCount: 0
    })
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<Error | null>(null)
    const loadingRef = useRef(false)
    const processedEvents = useRef<Set<string>>(new Set())
    // Track which platform each conversation belongs to (populated via socket events)
    const platformByConversation = useRef<Record<string, string>>({})

    // If BusinessAccountProvider or ChatProvider is not available, return default values
    if (!businessAccountContext || !chatContext) {
        const defaultContext: UnreadMessagesContextType = {
            totalUnreadCount: 0,
            totalUnreadConversations: 0,
            unreadCounts: {},
            unreadByPlatform: {},
            isLoading: false,
            error: null,
            markConversationAsRead: async () => { },
            incrementUnreadCount: () => { },
            getUnreadCount: () => 0,
            hasUnreadMessages: () => false,
            refreshUnreadCounts: async () => { },
        }
        return (
            <UnreadMessagesContext.Provider value={defaultContext}>
                {children}
            </UnreadMessagesContext.Provider>
        )
    }

    const { selectedAccountId: businessAccountId } = businessAccountContext
    const { socket, isConnected } = chatContext

    // Derived per-platform unread conversation counts (reactive)
    const [unreadByPlatform, setUnreadByPlatform] = useState<Record<string, number>>({ whatsapp: 0, facebook: 0, instagram: 0 })

    const normalizePlatform = (conv: any): string => {
        const p = (conv.platform || conv.channel || '').toLowerCase()
        if (p === 'facebook' || p === 'fb') return 'facebook'
        if (p === 'instagram' || p === 'ig') return 'instagram'
        if (p === 'whatsapp' || p === 'wa') return 'whatsapp'
        if (conv.last_message?.facebook_message_id || conv.facebook_message_id) return 'facebook'
        if (conv.last_message?.instagram_message_id || conv.instagram_message_id) return 'instagram'
        return 'whatsapp'
    }

    const calculateUnreadByPlatform = (byConversation: Record<string, number>): Record<string, number> => {
        const result: Record<string, number> = { whatsapp: 0, facebook: 0, instagram: 0 }
        Object.entries(byConversation).forEach(([convId, count]) => {
            if ((count || 0) > 0) {
                const platform = platformByConversation.current[convId] || 'whatsapp'
                result[platform] = (result[platform] || 0) + 1
            }
        })
        return result
    }

    // Fetch aggregate badge counts on mount and when businessAccountId changes.
    // One SQL index-scan returning 4 numbers — no rows, no LIMIT, no JOINs.
    // Safe for any account size and for serverless (RDS Data API) mode.
    useEffect(() => {
        if (!businessAccountId || loadingRef.current) return

        loadingRef.current = true
        setIsLoading(true)
        setError(null)

        const load = async () => {
            try {
                const res = await conversationsApi.getUnreadSummary(businessAccountId)
                const summary = (res as any)?.data ?? res
                const conversationsCount = Number(summary?.unreadConversationCount) || 0
                const total = Number(summary?.unreadMessageCount) || 0
                setUnreadCounts({ total, byConversation: {}, conversationsCount })
                // Platform breakdown starts at 0; per-conv tracking fills in via sockets
                setUnreadByPlatform({ whatsapp: 0, facebook: 0, instagram: 0 })
            } catch (err: any) {
                setError(err)
                setUnreadCounts({ total: 0, byConversation: {}, conversationsCount: 0 })
                setUnreadByPlatform({ whatsapp: 0, facebook: 0, instagram: 0 })
            } finally {
                setIsLoading(false)
                loadingRef.current = false
            }
        }

        load()
    }, [businessAccountId])

    // Increment unread count for a conversation (called by socket new-message handler).
    // Also adjusts the aggregate conversationsCount / total so badges stay accurate
    // without a re-fetch.
    const incrementUnreadCount = useCallback((conversationId: string, platform?: string) => {
        if (platform) {
            platformByConversation.current[conversationId] = platform
        }
        setUnreadCounts(prev => {
            const prevConvCount = prev.byConversation[conversationId] || 0
            const newConvCount = prevConvCount + 1
            const newByConversation = { ...prev.byConversation, [conversationId]: newConvCount }

            // If this conversation just became unread (was 0), bump conversationsCount
            const conversationsCount = prevConvCount === 0
                ? prev.conversationsCount + 1
                : prev.conversationsCount

            setUnreadByPlatform(calculateUnreadByPlatform(newByConversation))
            return { total: prev.total + 1, byConversation: newByConversation, conversationsCount }
        })
    }, [])

    // Setup Socket.IO listeners for real-time unread count updates
    useEffect(() => {
        if (!socket || !isConnected) return

        const handleConversationUpdate = (data: any) => {
            const conversationId = data.conversationId || data.conversation_id
            if (!conversationId) return

            if (data.platform) {
                platformByConversation.current[conversationId] = normalizePlatform(data)
            }

            const messageId = data.messageId || data.message_id
            const eventKey = messageId
                ? `msg-${conversationId}-${messageId}`
                : `conv-${conversationId}-${data.timestamp || Date.now()}`

            if (processedEvents.current.has(eventKey)) return
            processedEvents.current.add(eventKey)

            const unreadCountFromEvent = data.unread_count !== undefined && data.unread_count !== null
                ? Number(data.unread_count)
                : null

            if (data.type === 'messages_read' || (data.status === 'READ' && data.batchUpdate)) {
                const newCount = unreadCountFromEvent !== null ? unreadCountFromEvent : 0
                setUnreadCounts(prev => {
                    const prevCount = prev.byConversation[conversationId] || 0
                    const delta = prevCount - newCount
                    const newByConversation = { ...prev.byConversation, [conversationId]: newCount }
                    const conversationsCount = newCount === 0 && prevCount > 0
                        ? Math.max(0, prev.conversationsCount - 1)
                        : prev.conversationsCount
                    setUnreadByPlatform(calculateUnreadByPlatform(newByConversation))
                    return {
                        total: Math.max(0, prev.total - delta),
                        byConversation: newByConversation,
                        conversationsCount
                    }
                })
            } else if (data.type === 'new_message' && (data.isInbound || data.is_inbound)) {
                setUnreadCounts(prev => {
                    const oldCount = prev.byConversation[conversationId] || 0
                    let newCount: number
                    if (unreadCountFromEvent !== null) {
                        newCount = unreadCountFromEvent > oldCount ? unreadCountFromEvent : oldCount + 1
                    } else {
                        newCount = oldCount + 1
                    }
                    const newByConversation = { ...prev.byConversation, [conversationId]: newCount }
                    const conversationsCount = oldCount === 0
                        ? prev.conversationsCount + 1
                        : prev.conversationsCount
                    setUnreadByPlatform(calculateUnreadByPlatform(newByConversation))
                    return { total: prev.total + (newCount - oldCount), byConversation: newByConversation, conversationsCount }
                })
            } else if (unreadCountFromEvent !== null) {
                // IMPORTANT: Do NOT update unread count for outbound bot/agent messages.
                // When a bot responds instantly after an inbound message, it emits
                // conversation_updated with isInbound=false and unread_count fetched from
                // DynamoDB — which may still be 0 due to a race condition. Only inbound
                // messages should increment, and only 'messages_read' should decrement.
                if (data.type === 'new_message' && !(data.isInbound || data.is_inbound)) {
                    return
                }
                setUnreadCounts(prev => {
                    const prevCount = prev.byConversation[conversationId] || 0
                    const newByConversation = { ...prev.byConversation, [conversationId]: unreadCountFromEvent }
                    const conversationsCount = unreadCountFromEvent === 0 && prevCount > 0
                        ? Math.max(0, prev.conversationsCount - 1)
                        : unreadCountFromEvent > 0 && prevCount === 0
                            ? prev.conversationsCount + 1
                            : prev.conversationsCount
                    const totalDelta = unreadCountFromEvent - prevCount
                    setUnreadByPlatform(calculateUnreadByPlatform(newByConversation))
                    return {
                        total: Math.max(0, prev.total + totalDelta),
                        byConversation: newByConversation,
                        conversationsCount
                    }
                })
            }

            setTimeout(() => processedEvents.current.delete(eventKey), 10000)
        }

        const handleMessageStatusUpdate = (data: any) => {
            const conversationId = data.conversationId || data.conversation_id
            if (!conversationId) return

            if (data.status === 'READ' && data.batchUpdate) {
                const unreadCountFromEvent = data.unread_count !== undefined && data.unread_count !== null
                    ? Number(data.unread_count)
                    : null

                setUnreadCounts(prev => {
                    const prevCount = prev.byConversation[conversationId] || 0
                    const newCount = unreadCountFromEvent !== null ? unreadCountFromEvent : 0
                    const delta = prevCount - newCount
                    const newByConversation = { ...prev.byConversation, [conversationId]: newCount }
                    const conversationsCount = newCount === 0 && prevCount > 0
                        ? Math.max(0, prev.conversationsCount - 1)
                        : prev.conversationsCount
                    setUnreadByPlatform(calculateUnreadByPlatform(newByConversation))
                    return { total: Math.max(0, prev.total - delta), byConversation: newByConversation, conversationsCount }
                })
            }
        }

        const handleNewMessage = (data: any) => {
            const payload = data?.message ?? data
            const isInbound = payload.isInbound || payload.is_inbound ||
                ((payload.platform === 'instagram' || payload.platform === 'facebook') && !payload.isFromBusiness)
            if (!isInbound) return

            const conversationId = payload.conversationId || payload.conversation_id
            if (!conversationId) return

            const messageId = payload.messageId || payload.message_id || payload.id
            const eventKey = messageId
                ? `msg-${conversationId}-${messageId}`
                : `new-msg-${conversationId}-${payload.timestamp || Date.now()}`

            if (processedEvents.current.has(eventKey)) return

            processedEvents.current.add(eventKey)
            incrementUnreadCount(conversationId, payload.platform ? normalizePlatform(payload) : undefined)

            setTimeout(() => processedEvents.current.delete(eventKey), 10000)
        }

        socket.on('new-message', handleNewMessage)
        socket.on('conversation_updated', handleConversationUpdate)
        socket.on('message_status_update', handleMessageStatusUpdate)

        return () => {
            socket.off('new-message', handleNewMessage)
            socket.off('conversation_updated', handleConversationUpdate)
            socket.off('message_status_update', handleMessageStatusUpdate)
        }
    }, [socket, isConnected, businessAccountId, incrementUnreadCount])

    const markConversationAsRead = useCallback(async (conversationId: string) => {
        if (!businessAccountId) return

        const previousCount = unreadCounts.byConversation[conversationId] || 0

        try {
            if (previousCount > 0) {
                setUnreadCounts(prev => {
                    const newByConversation = { ...prev.byConversation, [conversationId]: 0 }
                    const conversationsCount = Math.max(0, prev.conversationsCount - 1)
                    setUnreadByPlatform(calculateUnreadByPlatform(newByConversation))
                    return {
                        total: Math.max(0, prev.total - previousCount),
                        byConversation: newByConversation,
                        conversationsCount
                    }
                })
            }

            await messagesApi.markConversationAsRead(conversationId, businessAccountId)
            dismissConversationNotifications(conversationId).catch(() => {})
        } catch (error) {
            console.error('[UnreadMessages] Failed to mark conversation as read, reverting:', error)
            if (previousCount > 0) {
                setUnreadCounts(prev => {
                    const newByConversation = { ...prev.byConversation, [conversationId]: previousCount }
                    const conversationsCount = prev.conversationsCount + 1
                    setUnreadByPlatform(calculateUnreadByPlatform(newByConversation))
                    return { total: prev.total + previousCount, byConversation: newByConversation, conversationsCount }
                })
            }
        }
    }, [businessAccountId, unreadCounts.byConversation])

    const getUnreadCount = useCallback((conversationId: string) => {
        return unreadCounts.byConversation[conversationId] || 0
    }, [unreadCounts.byConversation])

    const hasUnreadMessages = useCallback((conversationId: string) => {
        return (unreadCounts.byConversation[conversationId] || 0) > 0
    }, [unreadCounts.byConversation])

    const refreshUnreadCounts = useCallback(async () => {
        if (!businessAccountId || loadingRef.current) return

        loadingRef.current = true
        setIsLoading(true)
        setError(null)

        try {
            const res = await conversationsApi.getUnreadSummary(businessAccountId)
            const summary = (res as any)?.data ?? res
            const conversationsCount = Number(summary?.unreadConversationCount) || 0
            const total = Number(summary?.unreadMessageCount) || 0
            // Reset per-conv tracking on manual refresh — socket will repopulate deltas
            setUnreadCounts({ total, byConversation: {}, conversationsCount })
            setUnreadByPlatform({ whatsapp: 0, facebook: 0, instagram: 0 })
        } catch (err: any) {
            setError(err)
        } finally {
            setIsLoading(false)
            loadingRef.current = false
        }
    }, [businessAccountId])

    const value = useMemo<UnreadMessagesContextType>(() => ({
        totalUnreadCount: unreadCounts.total,
        totalUnreadConversations: unreadCounts.conversationsCount,
        unreadCounts: unreadCounts.byConversation,
        unreadByPlatform,
        isLoading,
        error,
        markConversationAsRead,
        incrementUnreadCount,
        getUnreadCount,
        hasUnreadMessages,
        refreshUnreadCounts,
    }), [
        unreadCounts.total,
        unreadCounts.conversationsCount,
        unreadCounts.byConversation,
        unreadByPlatform,
        isLoading,
        error,
        markConversationAsRead,
        incrementUnreadCount,
        getUnreadCount,
        hasUnreadMessages,
        refreshUnreadCounts,
    ])

    return (
        <UnreadMessagesContext.Provider value={value}>
            {children}
        </UnreadMessagesContext.Provider>
    )
}

export function useUnreadMessages() {
    const context = useContext(UnreadMessagesContext)
    if (context === undefined) {
        return {
            totalUnreadCount: 0,
            totalUnreadConversations: 0,
            unreadCounts: {},
            unreadByPlatform: { whatsapp: 0, facebook: 0, instagram: 0 },
            isLoading: false,
            error: null,
            markConversationAsRead: async () => { },
            incrementUnreadCount: () => { },
            getUnreadCount: () => 0,
            hasUnreadMessages: () => false,
            refreshUnreadCounts: async () => { },
        }
    }
    return context
}
